import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { FieldGroupState, FieldState, FilterFieldDefinition, FilterFieldGroupDefinition } from '../../../models';
import { DataFilterManagerService, FilterDefinitionStoreService, FilterPanelFacadeService } from '../../../services';
import { DataManipulationSelectors, FilterPanelSelectors } from '../../../store/selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-data-filter-panel',
  templateUrl: './data-filter-panel.component.html',
  styleUrls: ['./data-filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DataFilterPanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  readonly shouldExpandSections$ = new BehaviorSubject<boolean>(false);

  @HostBinding('class')
  private classes = 'flex flex-col gap-2 overflow-hidden h-full w-85 rounded-tl-xl bg-white shadow-sm';

  currentfilterInstanceId: string;
  fields$: Observable<(FieldState | FieldGroupState)[]>;
  currentFilterInstanceId$: Observable<string>;
  chosenOptionsCount$: Observable<number>;
  maxVisibleSectionsAmount: number;

  constructor(
    private store: Store,
    private filterPanelFacadeService: FilterPanelFacadeService,
    private dataFilterManagerService: DataFilterManagerService,
    private cd: ChangeDetectorRef,
    private filterDefinitionStoreService: FilterDefinitionStoreService,
  ) {}

  ngOnInit(): void {
    this.store
      .select(FilterPanelSelectors.selectCurrentInstanceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filterInstanceId) => {
        this.currentfilterInstanceId = filterInstanceId;
        this.fields$ = combineLatest([
          this.filterDefinitionStoreService.get(filterInstanceId).pipe(
            tap((config) => (this.maxVisibleSectionsAmount = config?.maxVisibleSectionsAmount)),
            filter((config) => !!config?.fields?.length),
          ),
          this.store.select(DataManipulationSelectors.Filter.createFilteringFieldsDictionarySelector(filterInstanceId)),
        ]).pipe(
          map(([config, fieldStateDict]) =>
            config.fields
              .map((fieldOrGroup) => {
                if (this.isFieldsGroup(fieldOrGroup)) {
                  const item = fieldOrGroup as FilterFieldGroupDefinition<any>;

                  return {
                    groupId: item.groupId,
                    translationKey: item.translationKey,
                    groupFields: item.groupFields.map((item) => fieldStateDict[item.fieldId]).filter(Boolean),
                    expanded: item.expanded,
                  } as FieldGroupState;
                }

                const item = fieldOrGroup as FilterFieldDefinition<any>;

                return fieldStateDict[item.fieldId];
              })
              .filter(Boolean),
          ),
        );
        this.chosenOptionsCount$ = this.store.pipe(
          select(DataManipulationSelectors.Filter.createChosenOptionsSelector(filterInstanceId)),
          map((chosenOptions) => chosenOptions.length),
        );
        this.cd.markForCheck();
      });
  }

  changeExpandState(): void {
    this.shouldExpandSections$.next(!this.shouldExpandSections$.value);
  }

  fieldTrackBy(_: number, item: FieldState | FieldGroupState): string {
    return 'groupFields' in item ? (item as FieldGroupState).groupId : (item as FieldState).id;
  }

  buildTranslationKey(relativeKey: string): string {
    return `core.dataFilter.${relativeKey}`;
  }

  close(): void {
    this.filterPanelFacadeService.close();
  }

  reset(): void {
    this.dataFilterManagerService.reset(this.currentfilterInstanceId);
  }

  isFieldsGroup(item: any): boolean {
    return 'groupFields' in item;
  }

  isField(item: any): boolean {
    return 'id' in item;
  }
}
